import { resolveId } from '@medplum/core'
import type {
  CareTeam,
  CareTeamParticipant,
  CodeableConcept,
  Group,
  Patient,
  Period,
  Practitioner,
  Reference,
  Resource,
} from '@medplum/fhirtypes'
import { isAfter } from 'date-fns'
import { difference, uniq } from 'lodash-es'
import {
  codeFromCodeableConcept,
  hasCodeableConcept,
} from '#src/codeableConcept.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import {
  CARE_TEAM_CATEGORIES,
  getRoleConcepts,
  type IDTRole,
  idtRoles,
} from '#src/models/care-teams/constants.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType, now } from '#src/utils.ts'

export class IDT extends ExtendedResource<CareTeam> {
  static readonly RESOURCE_TYPE = 'CareTeam'

  static isInstance(resource: Resource): resource is CareTeam {
    return (
      resource.resourceType === 'CareTeam' &&
      hasCodeableConcept(resource.category, CARE_TEAM_CATEGORIES.IDT)
    )
  }

  static isParticipantActive(participant: CareTeamParticipant): boolean {
    return IDT.isParticipantActiveAt(participant, now())
  }

  static isParticipantActiveAt(
    participant: CareTeamParticipant,
    activeAt: string
  ): boolean {
    if (
      participant.period?.start &&
      isAfter(participant.period.start, activeAt)
    ) {
      return false
    }
    if (participant.period?.end && !isAfter(participant.period.end, activeAt)) {
      return false
    }
    return true
  }

  constructor({
    members,
    participant,
  }: {
    members: {
      practitioner: Reference<Practitioner>
      role: IDTRole
      period?: Period
    }[]
    participant: Reference<Patient>
  }) {
    super({
      category: [CARE_TEAM_CATEGORIES.IDT],
      name: 'Interdisciplinary Team (IDT)',
      status: 'active',
      participant: members.map((member) => ({
        member: member.practitioner,
        role: getRoleConcepts([member.role]),
        period: {
          start: now(),
          ...member.period,
        },
      })),
      subject: participant,
    })
  }

  get members(): {
    practitioner: Reference<Practitioner>
    role: CodeableConcept[]
  }[] {
    return this.membersAt(now())
  }

  membersAt(activeAt: string): {
    practitioner: Reference<Practitioner>
    role: CodeableConcept[]
  }[] {
    return (
      this.resource.participant
        ?.filter((participant) =>
          IDT.isParticipantActiveAt(participant, activeAt)
        )
        ?.map((participant) => {
          invariant(
            isOfType(participant.member, 'Practitioner'),
            'Care team member must be a Practitioner'
          )
          invariant(participant.role, 'Care team member must have a role')
          return {
            practitioner: participant.member,
            role: participant.role,
          }
        }) ?? []
    )
  }

  /**
   * Every membership ever recorded (active, ended, and future-dated).
   * Malformed historical entries (non-Practitioner member or missing role)
   * are skipped rather than thrown so history views degrade gracefully on
   * legacy data.
   */
  get memberHistory(): {
    practitioner: Reference<Practitioner>
    role: CodeableConcept[]
    period?: Period
  }[] {
    return (this.resource.participant ?? []).flatMap((participant) => {
      if (!isOfType(participant.member, 'Practitioner') || !participant.role) {
        return []
      }
      return [
        {
          practitioner: participant.member,
          role: participant.role,
          period: participant.period,
        },
      ]
    })
  }
  getMemberByPractitioner(practitioner: Reference<Practitioner>):
    | {
        practitioner: Reference<Practitioner>
        role: CodeableConcept[]
      }
    | undefined {
    return this.members.find(
      (m) => m.practitioner.reference === practitioner.reference
    )
  }
  getMemberByRole(
    role: IDTRole,
    options?: {
      activeAt?: string
    }
  ):
    | {
        practitioner: Reference<Practitioner>
        role: CodeableConcept[]
      }
    | undefined {
    const activeAt = options?.activeAt ?? now()

    const participant = this.resource.participant?.find(
      (member) =>
        member.role?.some((r) => codeFromCodeableConcept(r) === role) &&
        IDT.isParticipantActiveAt(member, activeAt)
    )

    if (!participant) {
      return undefined
    }

    invariant(
      isOfType(participant.member, 'Practitioner'),
      'Care team member must be a Practitioner'
    )
    invariant(participant.role, 'Care team member must have a role')

    return {
      practitioner: participant.member,
      role: participant.role,
    }
  }
  setMemberByRole(
    role: IDTRole,
    practitioner: Reference<Practitioner>,
    options?: {
      asOf?: string
    }
  ): void {
    const effectiveAt = options?.asOf ?? now()
    const participants = this.resource.participant ?? []
    const index = participants.findIndex(
      (m) =>
        m.role?.some((r) => codeFromCodeableConcept(r) === role) &&
        IDT.isParticipantActiveAt(m, effectiveAt)
    )

    // If the same practitioner is already assigned to this role and is active (idempotent check)
    if (
      index !== -1 &&
      practitioner.reference === participants[index].member?.reference &&
      IDT.isParticipantActiveAt(participants[index], effectiveAt)
    ) {
      // Already assigned and active, no changes needed
      return
    }

    // If a different practitioner is assigned to this role
    if (
      index !== -1 &&
      practitioner.reference !== participants[index].member?.reference
    ) {
      // mark existing member as inactive
      participants[index].period = {
        ...participants[index].period,
        end: effectiveAt,
      }
    }

    // Add the new practitioner
    participants.push({
      member: practitioner,
      role: getRoleConcepts([role]),
      period: {
        start: effectiveAt,
      },
    })
    this.resource.participant = participants
  }

  get memberReferences(): Reference<Practitioner>[] {
    return this.members.map((member) => member.practitioner)
  }

  participantsByRole(role: IDTRole, activeOnly = true): CareTeamParticipant[] {
    return (
      this.resource.participant?.filter(
        (participant) =>
          participant.role?.some((r) => codeFromCodeableConcept(r) === role) &&
          (!activeOnly || IDT.isParticipantActive(participant))
      ) ?? []
    )
  }

  get participants(): CareTeamParticipant[] {
    return this.resource.participant ?? []
  }

  set participants(participants: CareTeamParticipant[]) {
    this.resource.participant = participants
  }

  get status(): CareTeam['status'] {
    return this.resource.status
  }

  get name(): string | undefined {
    return this.resource.name
  }

  get categoryCodes(): string[] | undefined {
    return this.resource.category?.map((c) => codeFromCodeableConcept(c) ?? '')
  }

  get memberDetails(): {
    practitionerId: string | undefined
    practitionerName: string | undefined
    roleCodes: IDTRole[]
  }[] {
    return this.members.map((member) => ({
      practitionerId: resolveId(member.practitioner),
      practitionerName: member.practitioner.display,
      roleCodes: member.role.map((c) => codeFromCodeableConcept(c) as IDTRole),
    }))
  }

  get memberRoles(): IDTRole[] {
    return uniq(
      this.members.flatMap((member) =>
        member.role.map((c) => codeFromCodeableConcept(c))
      )
    ) as IDTRole[]
  }

  get isValid(): boolean {
    return difference(idtRoles, this.memberRoles).length === 0
  }

  get periodStart(): string | undefined {
    return this.resource.period?.start
  }

  get periodEnd(): string | undefined {
    return this.resource.period?.end
  }

  get subject(): Reference<Group | Patient> | undefined {
    return this.resource.subject
  }

  static byParticipant(participant: Reference<Patient>): QueryTypes {
    return {
      category: [CARE_TEAM_CATEGORIES.IDT],
      subject: participant,
    }
  }

  static byParticipants(participants: Reference<Patient>[]): QueryTypes {
    return {
      category: [CARE_TEAM_CATEGORIES.IDT],
      subject: participants
        .map((participant) => participant.reference)
        .join(','),
      _count: '1000',
    }
  }

  static baseSearchQuery(): QueryTypes {
    return {
      category: [CARE_TEAM_CATEGORIES.IDT],
      _count: '1000',
    }
  }

  protected getExportFields() {
    return {
      id: this.id,
      status: this.status,
      name: this.name,
      category: this.categoryCodes,
      members: this.memberDetails,
      start: this.periodStart,
      end: this.periodEnd,
      patientId: resolveId(this.subject),
    }
  }
}
